import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  AbstractFormDialogData,
  AbstractFormDialog,
  AbstractDialogResultData,
  AgrDialogModule,
} from '@next/core-lib/dialog';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';

import { stepValidator } from '../../../../shared/validators';

export interface ThresholdSettingsDialogInputData extends AbstractFormDialogData {
  minAgeWithoutMeasurement?: number;
  minAgeWithMeasurement?: number;
  minimumHeightWithMeasurement?: number;
}

export interface ThresholdSettingsDialogResultData {
  minAgeWithoutMeasurement: number;
  minAgeWithMeasurement: number;
  minimumHeightWithMeasurement: number;
}

const noDecimalNumberPattern = /^\d+$/;

function minAgeWithMeasurementShouldBeSmallerThanWithoutMeasurement(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group = control.parent;
    if (!group) {
      return null;
    }

    const minAgeWithMeasurement = group.get('minAgeWithMeasurement')?.value;
    const minAgeWithoutMeasurement = group.get('minAgeWithoutMeasurement')?.value;

    if (
      minAgeWithMeasurement == null ||
      minAgeWithoutMeasurement == null ||
      minAgeWithMeasurement === '' ||
      minAgeWithoutMeasurement === ''
    ) {
      return null;
    }

    const withMeasurement = Number(minAgeWithMeasurement);
    const withoutMeasurement = Number(minAgeWithoutMeasurement);

    if (Number.isNaN(withMeasurement) || Number.isNaN(withoutMeasurement)) {
      return null;
    }

    return withMeasurement < withoutMeasurement ? null : { ageRange: true };
  };
}

@Component({
  selector: 'agr-threshold-settings-dialog',
  templateUrl: './threshold-settings-dialog.component.html',
  styleUrls: ['./threshold-settings-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AgrI18nSharedModule,
    AgrDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class ThresholdSettingsDialogComponent extends AbstractFormDialog<ThresholdSettingsDialogResultData> {
  protected mdDialogRef =
    inject<
      MatDialogRef<
        ThresholdSettingsDialogComponent,
        AbstractDialogResultData<ThresholdSettingsDialogResultData>
      >
    >(MatDialogRef);
  data = inject<ThresholdSettingsDialogInputData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  private readonly DEFAULT_MINIMUM_AGE_INSEMINATE_NOW = 15;
  private readonly DEFAULT_MINIMUM_AGE_WITH_MEASUREMENT = 12;
  private readonly DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT = 135;

  formGroup = this.fb.group(
    {
      minAgeWithoutMeasurement: [
        this.data.minAgeWithoutMeasurement ?? this.DEFAULT_MINIMUM_AGE_INSEMINATE_NOW,
        [Validators.required, Validators.min(0), Validators.max(30), stepValidator(0.5)],
      ],
      minAgeWithMeasurement: [
        this.data.minAgeWithMeasurement ?? this.DEFAULT_MINIMUM_AGE_WITH_MEASUREMENT,
        [Validators.required, Validators.min(0), Validators.max(30), stepValidator(0.5)],
      ],
      minimumHeightWithMeasurement: [
        this.data.minimumHeightWithMeasurement ?? this.DEFAULT_MINIMUM_HEIGHT_WITH_MEASUREMENT,
        [
          Validators.required,
          Validators.min(75),
          Validators.max(175),
          Validators.pattern(noDecimalNumberPattern),
        ],
      ],
    },
    { validators: [minAgeWithMeasurementShouldBeSmallerThanWithoutMeasurement()] },
  );
}
